/**
 * Fenêtre d'inscription à la newsletter (snippets/newsletter-popup.liquid).
 *
 * Elle s'ouvre une seule fois par visiteur : après le délai réglé, compté depuis la première page de
 * la visite, ou plus tôt sur ordinateur quand la souris quitte la page par le haut. Elle attend que
 * rien d'autre n'occupe l'écran : bannière cookies, tiroir ou menu ouvert, saisie en cours, onglet
 * en arrière-plan. Non modale : la page reste utilisable ; la croix ou Échap la ferment.
 *
 * Au retour de l'envoi du formulaire (…?customer_posted=true#NewsletterPopupForm), elle se rouvre
 * sur la confirmation ou l'erreur rendue par Shopify.
 */

const SEEN_KEY = 'sm-newsletter-popup-seen';
const VISIT_KEY = 'sm-newsletter-visit-start';

/** Never right at page load, even when the visit delay has already run out on an earlier page. */
const MIN_PAGE_DELAY = 5000;
/** Wait before trying again when something else holds the screen. */
const RETRY_DELAY = 5000;

/**
 * Storage can be missing or throw (private browsing, blocked site data): undefined then.
 * @param {'localStorage' | 'sessionStorage'} area
 * @param {string} key
 */
function read(area, key) {
  try {
    return window[area].getItem(key);
  } catch {
    return undefined;
  }
}

/**
 * @param {'localStorage' | 'sessionStorage'} area
 * @param {string} key
 * @param {string} value
 */
function write(area, key, value) {
  try {
    window[area].setItem(key, value);
  } catch {
    // Nothing to do: without storage, the popup is never shown automatically.
  }
}

class NewsletterPopup extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLElement | null} */
  #returnFocus = null;
  /** @type {number | undefined} */
  #timer;
  #pageStart = Date.now();

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#dialog.addEventListener('keydown', this.#onKeydown);
    this.querySelector('[data-newsletter-close]')?.addEventListener('click', this.close);

    // Back from the form post: show the outcome where the visitor left it.
    if (window.location.hash === `#${this.dataset.formId}` && this.querySelector('[data-newsletter-message]')) {
      this.#open(true);
      return;
    }

    // Already shown once, or no storage to remember it: stay closed.
    if (read('localStorage', SEEN_KEY) !== null) return;

    const delay = (Number(this.dataset.delay) || 30) * 1000;
    let visitStart = Number(read('sessionStorage', VISIT_KEY));
    if (!visitStart) {
      visitStart = Date.now();
      write('sessionStorage', VISIT_KEY, String(visitStart));
    }
    const wait = Math.max(delay - (Date.now() - visitStart), MIN_PAGE_DELAY);
    this.#timer = window.setTimeout(this.#tryOpen, wait);

    if (this.dataset.exitIntent === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mouseout', this.#onMouseOut);
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    document.removeEventListener('mouseout', this.#onMouseOut);
  }

  close = () => {
    if (!this.#dialog?.open) return;
    this.#dialog.close();
    this.#returnFocus?.focus({ preventScroll: true });
    this.#returnFocus = null;

    // A reload must not bring the confirmation back.
    if (window.location.hash === `#${this.dataset.formId}`) {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('customer_posted');
      window.history.replaceState(window.history.state, '', url);
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} event */
  #onMouseOut = (event) => {
    if (event.relatedTarget || event.clientY > 0) return;
    if (Date.now() - this.#pageStart < MIN_PAGE_DELAY) return;
    this.#tryOpen(false);
  };

  /** @param {boolean} [retry] - try again later when the screen is busy (timer only) */
  #tryOpen = (retry = true) => {
    if (!this.#dialog || this.#dialog.open || read('localStorage', SEEN_KEY) !== null) return;

    if (this.#screenIsBusy()) {
      if (retry) this.#timer = window.setTimeout(this.#tryOpen, RETRY_DELAY);
      return;
    }

    window.clearTimeout(this.#timer);
    document.removeEventListener('mouseout', this.#onMouseOut);
    write('localStorage', SEEN_KEY, String(Date.now()));
    this.#open(false);
  };

  #screenIsBusy() {
    if (document.visibilityState !== 'visible') return true;

    // Shopify's cookie banner comes first.
    const banner = document.getElementById('shopify-pc__banner');
    if (banner && banner.getClientRects().length > 0 && getComputedStyle(banner).visibility !== 'hidden') return true;

    // Cart drawer, search, quick add, menu.
    if (document.querySelector('dialog[open]:not(.newsletter-popup__dialog), details.menu-drawer-container[open]')) {
      return true;
    }

    // Typing in a field, or working inside an iframe (Kickflip configurator).
    const active = document.activeElement;
    return Boolean(active?.matches('input, textarea, select, iframe, [contenteditable="true"]'));
  }

  /** @param {boolean} focusMessage */
  #open(focusMessage) {
    if (!this.#dialog) return;

    this.#returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#clearPreviewBar();
    // Non-modal: focus goes to the close button, the first control.
    this.#dialog.show();

    if (focusMessage) {
      /** @type {HTMLElement | null} */ (this.querySelector('[data-newsletter-message]'))?.focus();
    }
  }

  // Shopify's preview bar sits at the bottom of theme previews: stay above it.
  #clearPreviewBar() {
    const preview = document.getElementById('PBarNextFrameWrapper');
    let offset = 0;
    if (preview) {
      const rect = preview.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom >= window.innerHeight - 1) {
        offset = Math.round(window.innerHeight - rect.top);
      }
    }
    this.style.setProperty('--newsletter-popup-offset', `${offset}px`);
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
